import React from 'react';
import { X, User, Phone, Mail, Calendar, Tag } from 'lucide-react';

interface Lead {
  id: string;
  full_name: string;
  phone: string;
  email?: string;
  created_at: string;
  lead_type?: string;
}

interface AdLeadsModalProps {
  isOpen: boolean;
  onClose: () => void;
  leads: Lead[];
  isLoading: boolean;
  onLeadClick?: (leadId: string) => void;
}

export default function AdLeadsModal({ isOpen, onClose, leads, isLoading, onLeadClick }: AdLeadsModalProps) {
  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getLeadTypeLabel = (type?: string) => {
    switch (type) {
      case 'buyer':
        return 'קונה';
      case 'seller':
        return 'מוכר';
      case 'investor':
        return 'משקיע';
      default:
        return 'לא צוין';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">לידים מהמודעה</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-88px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12">
              <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">אין לידים למודעה זו</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 mb-4">
                סך הכל: <span className="font-semibold text-gray-900">{leads.length}</span> לידים
              </div>
              {leads.map((lead) => (
                <div
                  key={lead.id}
                  className="bg-gray-50 rounded-lg p-5 border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => onLeadClick?.(lead.id)}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <User className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-500 mb-1">שם מלא</div>
                        <div className="text-base font-semibold text-gray-900 truncate">{lead.full_name}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-500 mb-1">טלפון</div>
                        <a href={`tel:${lead.phone}`} className="text-base font-semibold text-blue-600 hover:text-blue-700 truncate block" dir="ltr">
                          {lead.phone}
                        </a>
                      </div>
                    </div>

                    {lead.email && (
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-gray-500 mb-1">אימייל</div>
                          <a href={`mailto:${lead.email}`} className="text-base font-semibold text-blue-600 hover:text-blue-700 truncate block">
                            {lead.email}
                          </a>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-500 mb-1">תאריך</div>
                        <div className="text-base font-semibold text-gray-900">{formatDate(lead.created_at)}</div>
                      </div>
                    </div>

                    {lead.lead_type && (
                      <div className="flex items-start gap-3">
                        <Tag className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-gray-500 mb-1">סוג ליד</div>
                          <div className="text-base font-semibold text-gray-900">{getLeadTypeLabel(lead.lead_type)}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
